import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, ExternalLink, Search } from "lucide-react";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming",
];

export const ChapterFinder = ({ chapters: extChapters = null, selected: extSelected = null, onSelectState = null, showHeader = true }) => {
  const [intChapters, setIntChapters] = useState([]);
  const [intSelected, setIntSelected] = useState("");

  const controlled = extChapters !== null;
  const chapters = controlled ? extChapters : intChapters;
  const selected = extSelected !== null ? extSelected : intSelected;
  const setSelected = onSelectState || setIntSelected;

  useEffect(() => {
    if (!controlled) api.get("/chapters").then((r) => setIntChapters(r.data || [])).catch(() => {});
  }, [controlled]);

  const matches = useMemo(
    () => (chapters || []).filter((c) => (c.state || "").toLowerCase() === (selected || "").toLowerCase()),
    [chapters, selected]
  );

  return (
    <section className="bg-[#0A1128] text-white py-24 sm:py-32 grain" data-testid="chapter-finder-section" id="chapter-finder">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 relative z-10">
        {showHeader && (
        <div className="text-center mb-10">
          <span className="text-sm uppercase tracking-[0.3em] font-semibold text-[#F4A261]">Find Your Chapter</span>
          <h2 className="font-display text-4xl sm:text-6xl uppercase mt-4 leading-[0.95]">
            Stand With Patriots <span className="text-[#D90429]">Near You</span>
          </h2>
          <p className="text-white/70 text-lg mt-5 max-w-xl mx-auto leading-relaxed">
            Select your state to connect with your local chapter coordinator.
          </p>
        </div>
        )}

        <div className="max-w-md mx-auto flex items-center gap-3">
          <div className="flex-1">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger
                data-testid="chapter-state-select"
                className="bg-white/5 border-white/20 text-white h-14 text-base uppercase tracking-wide"
              >
                <SelectValue placeholder="Select your state" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {US_STATES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`chapter-state-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-14 h-14 flex items-center justify-center bg-[#D90429] shrink-0">
            <Search className="w-5 h-5" />
          </div>
        </div>

        {selected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-10"
            data-testid="chapter-results"
          >
            {matches.length ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {matches.map((c) => (
                  <div
                    key={c.id}
                    data-testid="chapter-card"
                    className="border border-white/15 p-6 hover:border-[#D90429] transition-colors duration-200"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 text-[#F4A261]">
                        <MapPin className="w-4 h-4" />
                        <span className="font-display text-xl uppercase text-white">
                          {c.city ? `${c.city}, ` : ""}{c.state}
                        </span>
                      </div>
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${c.status === "Forming" ? "bg-[#F4A261]/20 text-[#F4A261]" : "bg-[#D90429] text-white"}`}>
                        {c.status || "Active"}
                      </span>
                    </div>
                    {c.goal_reached && (
                      <span data-testid="finder-goal-reached" className="inline-block mt-2 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-gradient-to-r from-[#D90429] to-[#F4A261] text-white font-bold">Goal Reached 🎉</span>
                    )}
                    {c.name && <div className="text-white/80 mt-3 font-semibold">{c.name}</div>}
                    <div className="mt-4 space-y-2 text-sm text-white/70">
                      {c.contact_email && (
                        <a href={`mailto:${c.contact_email}`} className="flex items-center gap-2 hover:text-white">
                          <Mail className="w-4 h-4" /> {c.contact_email}
                        </a>
                      )}
                      {c.contact_phone && (
                        <a href={`tel:${c.contact_phone}`} className="flex items-center gap-2 hover:text-white">
                          <Phone className="w-4 h-4" /> {c.contact_phone}
                        </a>
                      )}
                      {c.link && (
                        <a href={c.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white">
                          <ExternalLink className="w-4 h-4" /> Visit chapter link
                        </a>
                      )}
                      {c.slug && (
                        <Link to={`/chapter/${c.slug}`} data-testid={`chapter-page-link-${c.slug}`} className="flex items-center gap-2 text-[#F4A261] hover:text-white font-semibold">
                          <ExternalLink className="w-4 h-4" /> Become a founding member
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-white/15 p-8 text-center" data-testid="chapter-empty">
                <p className="text-white/80 text-lg">
                  No active chapter in <span className="text-[#F4A261] font-semibold">{selected}</span> yet.
                </p>
                <p className="text-white/60 mt-2">
                  We're expanding fast. Join our newsletter and help launch the {selected} chapter.
                </p>
                <Link
                  to={`/start-a-chapter?state=${encodeURIComponent(selected)}`}
                  data-testid="chapter-empty-join"
                  className="inline-flex items-center gap-2 mt-6 bg-[#D90429] text-white px-8 py-3 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200"
                >
                  Start A Chapter
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
};
